import { ChangeDetectionStrategy, Component, effect, forwardRef, Inject, input, Optional, SkipSelf, viewChild, ViewEncapsulation } from "@angular/core";
import { ControlContainer, FormControl, FormGroup, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from "@angular/forms";
import { CommonModule } from "@angular/common";
import { VButtonComponent } from "../button/button.component";
import { TreeTable, TreeTableModule } from "primeng/treetable";
import { VCheckboxComponent } from "../checkbox/checkbox.component";
import { GcLayoutModule } from "src/app/components/v-layout/layout.module";
import { VVarDirective } from "src/app/components/v-var/v-var.directive";
import { Color } from "../util";
import { Dict } from "src/util/types/global";
import { sortBy } from "src/util/sort";
import { TableSelectConfig } from "./tableSelect.config";
import { FormWrapper, VContainerComponent } from "../wrapper.interface";
import { WRAPPER_TOKEN, V_COMPONENT_CONTAINER, V_FORM_CONTROL_TOKEN } from "../wrapper.tokens";
import { VIconComponent } from "src/app/components/v-icon/v-icon.component";

type Icon = {name: string, color: Color};
type SelectionMode = "all" | "some" | "none";
type PropFunction<T> = (x: T) => string;
/**
 * Input value of this component. 
 * Must have an ID and may have children of the same type.
 */
type Input = {
    ID?: string;
    children?: Input[];
};

/**
 * Converted value type.
 * A parent will add itself as its child. 
 * The parent will then become a "dummy", 
 * giving information about the selection state 
 * and offer interaction possibilities (like select all)
 * 
 * @remark  This node will appear as its own child, if it has children.
 *          In that case the title will be altered to "Select `XYZ`"
 * 
 * @example 
 * - ✔️ A
 * - ➖ B (Select all)
 *      - ✔️ Select 'B'
 *      - ✖️ B-A
 *      - ✖️ B-B
 * - ✔️ C
 */
type Value<T extends Input = Input> = T & {
    title?: string;
    hasChildren?: boolean;
    parent_ID?: string;
    /**
     * Reference to the parent version of this node.
    */
   parent?: Value<T>;
   /**
     * Children will have itself as reference, 
     * therefore we keep track of its children here
   */
  otherChildren?: Input[];
};

/**
 * Node as it appears on the tree table.
 * Only required to interact with treeTable.
 */
type RowNode<T extends Input> = {
    level: number;
    parent?: RowNode<T>;
    visible?: boolean;
    node: Value<T> & { expanded?: boolean };
};


/**
 * @ignore
 * TODO: pass formControl to checkboxes
 */
@Component({
    selector:    'v-tableSelect',
    templateUrl: './tableSelect.component.haml',
    styleUrls:   ['./tableSelect.component.sass'],
    providers:   [
        { provide: V_FORM_CONTROL_TOKEN, useExisting: forwardRef(() => VTableSelectComponent) },
        { provide: WRAPPER_TOKEN, useExisting: forwardRef(() => VTableSelectComponent) },
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => VTableSelectComponent), multi: true }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation:   ViewEncapsulation.None,
    imports:         [
        CommonModule, FormsModule, ReactiveFormsModule, GcLayoutModule, TreeTableModule,
        VButtonComponent, VCheckboxComponent, VVarDirective, VIconComponent,
    ]
})
export class VTableSelectComponent<T extends Input = Input> extends FormWrapper<TableSelectConfig, string[]> {
    protected _baseConfig: TableSelectConfig = TableSelectConfig.baseTheme.Default;
    private readonly values: Dict<Value> = {};

    public readonly data = input([], {
        transform: (value: T[]) => this.convertValue(value),
    });
    public readonly table = viewChild.required<TreeTable>("table");
    public readonly title = input<string | PropFunction<T>>("title");

    public form = new FormGroup({
        nodes: new FormControl<string[]>([])
    });

    constructor(
        @Optional() protected controlContainer: ControlContainer,
        @Inject(V_COMPONENT_CONTAINER) @Optional() @SkipSelf() protected parent?: VContainerComponent
    ) {
        super(controlContainer, parent);
        effect(() => this.toDict(this.data()));
        this.form.valueChanges.subscribe(x => this.writeValue(x.nodes));
    }

    public getIcon(mode: SelectionMode): Icon {
        if (mode === "all") return {name: "check", color: "success"};
        if (mode === "some") return {name: "minus", color: "text"};
        return {name: "xmark", color: "danger"};
    }

    public getSelectionMode(rowNode: RowNode<T>): SelectionMode {
        const childIDs = rowNode.node.children?.map(x => x.ID.startsWith(PARENT_MARKER) ? x.ID.substring(PARENT_MARKER.length) : x.ID) ?? [];
        if (!childIDs.length) return "none";

        const selectedNodes = this.form.value.nodes ?? [];
        if (childIDs.every(id => selectedNodes.includes(id))) return "all";
        if (childIDs.some(id => selectedNodes.includes(id))) return "some";

        // Check if any child node's selection mode is "some" or "all"
        const childValue = rowNode.node.children?.some(x =>
            this.getSelectionMode({ node: x as Value<T>, level: rowNode.level + 1 }) !== "none"
        );

        return childValue ? "some" : "none";
    }

    public selectChildren(rowNode: RowNode<T>, mode: SelectionMode) {
        const childIDs = rowNode.node.children.map(x =>
            x.ID.startsWith(PARENT_MARKER) ? x.ID.substring(PARENT_MARKER.length) : x.ID
        );
        const fc = this.form.get("nodes");
        let IDs = fc.value ?? [];

        if (mode === "all") {
            // Remove all child IDs
            const allChildIDs = childIDs.flatMap(id => this.getChildIDs(id)).filter((v, i, a) => a.indexOf(v) === i);
            IDs = IDs.filter(ID => !allChildIDs.includes(ID));
        } else {
            // Add child IDs
            IDs = Array.from(new Set([...IDs, ...childIDs]));
        }

        fc.setValue(IDs);
    }

    public toggleNode(rowNode: RowNode<T>, event?: Event) {
        rowNode.node.expanded = !rowNode.node.expanded;
        const table = this.table();

        if (rowNode.node.expanded) {
            table.onNodeExpand.emit({
                originalEvent: event,
                node:          rowNode.node
            });
        } else {
            table.onNodeCollapse.emit({
                originalEvent: event,
                node:          rowNode.node
            });
        }

        table.updateSerializedValue();
        table.tableService.onUIUpdate(table.value);

        event?.preventDefault();
    }

private convertValue(value?: T[], parent_ID?: string): Value[] {
    return (value ?? []).map((x: Value) => {
        // don't alter existing data
        const clonedX: Value = { ...x, parent_ID: parent_ID ? `parent_${parent_ID}` : undefined };

        if (!clonedX.children?.length) return clonedX;

        clonedX.children = sortBy(
            this.convertValue(clonedX.children as T[], clonedX.ID),
            child => !child.children.length,
            child => child.title.toLowerCase()
        );

        // Create a new parent instance
        const { children, ...parentData } = clonedX;
        const title = this.title();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const titleText = typeof title === "string" ? (clonedX as any)[title] : title(clonedX as T);

        const parentEntry: Value = {
            ...parentData,
            hasChildren:   !!children.length,
            title:         `'${titleText}' auswählen`,
            otherChildren: [...children]
        };

        clonedX.children.unshift(parentEntry);
        clonedX.ID = `${PARENT_MARKER}${clonedX.ID}`;

        return clonedX;
    });
}

    private toDict(values?: Value[]) {
        values?.forEach(value => {
            this.values[value.ID] = value;
            this.toDict(value.children);
        });
    }

    private getParentIDs(ID: string): string[] {
        const IDs = [];
        let parent = this.values[ID]?.parent_ID;
        while (parent) {
            IDs.push(parent.startsWith(PARENT_MARKER) ? parent.substring(PARENT_MARKER.length) : parent);
            parent = this.values[parent]?.parent_ID;
        }
        return IDs;
    }

    private getChildIDs(ID: string): string[] {
        const normalizedID = ID.startsWith(PARENT_MARKER) ? ID.substring(PARENT_MARKER.length) : ID;

        return [
            normalizedID,
            ...(this.values[normalizedID].otherChildren ?? [])
                .filter(x => x.ID !== normalizedID)
                .flatMap(x => this.getChildIDs(x.ID))
        ];
    }

    public handleChecked(checked: boolean, id: string) {
        if (!checked) return;
        this.form.get("nodes").setValue([...this.form.value.nodes, ...this.getParentIDs(id)].uniq());
    }
}

const PARENT_MARKER = "parent_";
